import { Link } from "react-router-dom";
import { LOGO_URL } from "@/config/branding";

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F5F5F7' }} data-testid="privacy-policy-page">
      {/* Nav */}
      <nav className="bg-white/95 backdrop-blur-md border-b border-[#E5E5EA] sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <Link to="/">
            <img src={LOGO_URL} alt="Self Certify UK" className="h-12 sm:h-14 object-contain object-left" />
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <Link to="/" className="text-sm font-medium hover:text-[#00A3E0] transition-colors" style={{ fontFamily: 'Outfit, sans-serif', color: '#8E8E93' }}>
              Home
            </Link>
            <Link to="/contact" className="text-sm font-medium hover:text-[#00A3E0] transition-colors" style={{ fontFamily: 'Outfit, sans-serif', color: '#8E8E93' }}>
              Contact
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif', color: '#1C1C1E' }}>
          Privacy Policy
        </h1>
        <p className="text-sm mb-10" style={{ color: '#8E8E93' }}>Last updated: {new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>

        <div className="space-y-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#1C1C1E' }}>
          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>1. Who We Are</h2>
            <p className="text-sm leading-relaxed text-[#3F3F46]">
              Self Certify UK ("we", "us", "our") operates the Self Certify UK building notification platform at selfcertifyuk.co.uk and the Self Certify UK mobile application. Our registered email address is selfcertifyuk@gmail.com. We are committed to protecting your privacy and handling your personal data responsibly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>2. What Data We Collect</h2>
            <p className="text-sm leading-relaxed text-[#3F3F46] mb-3">We collect the following personal data when you use our service:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-sm text-[#3F3F46]">
              <li><strong>Account information:</strong> Full name, company name, email address, and password (encrypted)</li>
              <li><strong>Qualification documents:</strong> Certificates and documents uploaded during the verification process</li>
              <li><strong>Notification data:</strong> Certificate reference numbers, installation addresses, descriptions of works completed, installation dates, and work item details</li>
              <li><strong>Customer data:</strong> Homeowner names, email addresses, and phone numbers as entered by registered technicians</li>
              <li><strong>Address data:</strong> UK postcodes and addresses looked up via the Postcodes.io service</li>
              <li><strong>Payment data:</strong> Payment transactions are processed securely by Stripe. We do not store your card details</li>
              <li><strong>Contact form submissions:</strong> Name, email, and message content when using the contact form</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>3. How We Use Your Data</h2>
            <p className="text-sm leading-relaxed text-[#3F3F46] mb-3">We use your personal data for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-sm text-[#3F3F46]">
              <li>To create and manage your account</li>
              <li>To verify your electrical qualifications before granting access to the platform</li>
              <li>To generate Building Notification certificates</li>
              <li>To email certificates to homeowners, technicians, and local authorities as part of the notification process</li>
              <li>To process payments via Stripe for the notification fee</li>
              <li>To respond to your enquiries submitted through the contact form</li>
              <li>To maintain a record of submitted building notifications in your dashboard</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>4. Legal Basis for Processing</h2>
            <p className="text-sm leading-relaxed text-[#3F3F46]">
              We process your personal data on the following legal bases under UK GDPR:
            </p>
            <ul className="list-disc pl-6 space-y-1.5 text-sm text-[#3F3F46] mt-3">
              <li><strong>Contract:</strong> Processing is necessary to provide our building notification service to you</li>
              <li><strong>Legitimate interest:</strong> To verify qualifications and maintain the integrity of our platform</li>
              <li><strong>Consent:</strong> Where you provide information via our contact form</li>
              <li><strong>Legal obligation:</strong> Where required to comply with UK building regulations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>5. Data Sharing</h2>
            <p className="text-sm leading-relaxed text-[#3F3F46] mb-3">We share your data with the following parties:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-sm text-[#3F3F46]">
              <li><strong>Local authorities:</strong> Building notification certificates are emailed to the relevant local authority building control department as part of the notification process</li>
              <li><strong>Homeowners:</strong> Certificates are sent to the homeowner email address provided by the technician</li>
              <li><strong>Stripe:</strong> Payment data is processed by Stripe Inc. in accordance with their privacy policy (stripe.com/privacy)</li>
              <li><strong>Resend:</strong> Email delivery is handled by Resend in accordance with their privacy policy</li>
              <li><strong>Postcodes.io:</strong> Postcode lookups are processed by Postcodes.io (an open-source service)</li>
            </ul>
            <p className="text-sm leading-relaxed text-[#3F3F46] mt-3">We do not sell your personal data to any third parties.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>6. Data Retention</h2>
            <p className="text-sm leading-relaxed text-[#3F3F46]">
              We retain your personal data for as long as your account is active. Building notification records are retained for a minimum of 6 years in line with UK building regulations requirements. If you request account deletion, we will remove your personal data within 30 days, except where we are required by law to retain it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>7. Data Security</h2>
            <p className="text-sm leading-relaxed text-[#3F3F46]">
              We implement appropriate technical and organisational measures to protect your personal data, including encrypted password storage, secure HTTPS connections, and access controls. Payment information is handled entirely by Stripe and is never stored on our servers.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>8. Your Rights</h2>
            <p className="text-sm leading-relaxed text-[#3F3F46] mb-3">Under UK GDPR, you have the following rights:</p>
            <ul className="list-disc pl-6 space-y-1.5 text-sm text-[#3F3F46]">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Rectification:</strong> Request correction of inaccurate data</li>
              <li><strong>Erasure:</strong> Request deletion of your data (subject to legal retention requirements)</li>
              <li><strong>Restriction:</strong> Request limitation of processing</li>
              <li><strong>Portability:</strong> Request transfer of your data in a machine-readable format</li>
              <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
            </ul>
            <p className="text-sm leading-relaxed text-[#3F3F46] mt-3">
              To exercise any of these rights, please contact us at <a href="mailto:selfcertifyuk@gmail.com" className="text-[#00A3E0] font-semibold hover:underline">selfcertifyuk@gmail.com</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>9. Cookies</h2>
            <p className="text-sm leading-relaxed text-[#3F3F46]">
              Our platform uses essential cookies for authentication purposes only. These cookies are necessary for the service to function and cannot be opted out of. We do not use advertising or tracking cookies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>10. Children's Privacy</h2>
            <p className="text-sm leading-relaxed text-[#3F3F46]">
              Our service is intended for use by qualified electricians aged 18 and over. We do not knowingly collect personal data from children under 18.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>11. Changes to This Policy</h2>
            <p className="text-sm leading-relaxed text-[#3F3F46]">
              We may update this privacy policy from time to time. Any changes will be posted on this page with an updated revision date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>12. Contact Us</h2>
            <p className="text-sm leading-relaxed text-[#3F3F46]">
              If you have any questions about this privacy policy or our data practices, please contact us:
            </p>
            <div className="mt-3 border-2 border-[#E4E4E7] p-4 bg-white text-sm">
              <p className="font-semibold">Self Certify UK</p>
              <p className="text-[#3F3F46]">Email: <a href="mailto:selfcertifyuk@gmail.com" className="text-[#00A3E0] hover:underline">selfcertifyuk@gmail.com</a></p>
              <p className="text-[#3F3F46]">Website: <a href="https://selfcertifyuk.co.uk" className="text-[#00A3E0] hover:underline">selfcertifyuk.co.uk</a></p>
            </div>
          </section>

          <section>
            <p className="text-sm leading-relaxed text-[#3F3F46]">
              If you are not satisfied with our response, you have the right to lodge a complaint with the Information Commissioner's Office (ICO) at <a href="https://ico.org.uk" className="text-[#00A3E0] hover:underline" target="_blank" rel="noopener noreferrer">ico.org.uk</a>.
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#1C1C1E] py-8 mt-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <img src={LOGO_URL} alt="Self Certify UK" className="h-8 object-contain" />
            <div className="flex items-center gap-6 text-sm text-white/40">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
              <Link to="/delete-account" className="hover:text-white transition-colors">Delete Account</Link>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-white/10 text-center text-xs text-white/30">
            &copy; {new Date().getFullYear()} Self Certify UK. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
